/**
 * Tenant Feature Flags Context (also drives "Site Sections" visibility)
 *
 * Org resolution: signed-in user's org -> subdomain tenant org -> master org.
 * Master org switches apply to everyone; a section is on only if no applicable org turned it off.
 * Re-fetched on every route change and on the 'features-updated' window event,
 * so admin toggles take effect immediately without a redeploy or reload.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { API_BASE, DISABLED_FEATURES } from '../config/api';

const API = API_BASE;
const MASTER_ORG_ID = 'org_navigate_master';

// Default feature flags (all sections on unless an admin turns them off)
const DEFAULT_FEATURES = {
  courses: true,
  external_courses: true,
  learning_labs: true,
  learning_pathways: true,
  masterclasses: true,
  programmes: true,
  campaigns: true,
  ebooks: true,
  wsp: true,
  certificates: true,
  wishlists: true,
  leaderboard: true,
  cpd_tracking: true,
  regulatory_alerts: true,
  navigate_academy: true,
  navi_coach: true,
  ai_course_generation: false,
};

export const ADMIN_ROLES = ['admin', 'org_admin', 'super_admin'];

const FeatureFlagsContext = createContext({
  features: DEFAULT_FEATURES,
  loading: true,
  orgId: MASTER_ORG_ID,
  isFeatureEnabled: () => true,
  isSectionEnabled: () => true,
  refreshFeatures: async () => {},
});

export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const { tenant } = useTenant() || {};
  const location = useLocation();
  const orgId = user?.organization_id || tenant?.orgId || MASTER_ORG_ID;
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);
  const orgRef = useRef(orgId);
  orgRef.current = orgId;

  const reqRef = useRef(0);

  const fetchFeatures = useCallback(async () => {
    const reqId = ++reqRef.current;
    try {
      // Master (Navigate) switches apply to every learner; an org can only switch more sections off
      const orgIds = [...new Set([MASTER_ORG_ID, orgRef.current])];
      const results = await Promise.all(orgIds.map((id) =>
        axios.get(`${API}/api/organizations/${id}/features`, { params: { _: Date.now() } })
          .then((r) => r.data?.features || {}).catch(() => ({}))
      ));
      const merged = Object.assign({ ...DEFAULT_FEATURES }, ...results);
      Object.keys(merged).forEach((k) => { if (results.some((x) => x[k] === false)) merged[k] = false; });
      if (reqId !== reqRef.current) return; // a newer request (e.g. after login resolved the org) wins
      setFeatures(merged);
    } catch (error) {
      if (reqId === reqRef.current) setFeatures(DEFAULT_FEATURES);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, []);

  // Org change + every navigation => toggles apply immediately
  useEffect(() => { fetchFeatures(); }, [orgId, location.pathname, fetchFeatures]);

  useEffect(() => {
    const onUpdated = () => fetchFeatures();
    window.addEventListener('features-updated', onUpdated);
    return () => window.removeEventListener('features-updated', onUpdated);
  }, [fetchFeatures]);

  // Section is on unless the org turned it off (or it is force-disabled in code)
  const isFeatureEnabled = useCallback((feature) => {
    if (DISABLED_FEATURES?.[feature]) return false;
    return features[feature] !== false;
  }, [features]);

  return (
    <FeatureFlagsContext.Provider value={{
      features,
      loading,
      orgId,
      isFeatureEnabled,
      isSectionEnabled: isFeatureEnabled,
      refreshFeatures: fetchFeatures,
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

/**
 * HOC to wrap components that should only render if feature is enabled
 */
export function withFeatureFlag(WrappedComponent, featureName, FallbackComponent = null) {
  return function FeatureFlaggedComponent(props) {
    const { isFeatureEnabled } = useFeatureFlags();
    if (!isFeatureEnabled(featureName)) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }
    return <WrappedComponent {...props} />;
  };
}

/**
 * Component that conditionally renders children based on feature flag
 */
export function FeatureFlag({ feature, children, fallback = null }) {
  const { isFeatureEnabled } = useFeatureFlags();
  if (!isFeatureEnabled(feature)) return fallback;
  return children;
}

export default FeatureFlagsContext;
